import { Box, Link, Stack, Typography } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import type { DirectMessageAttachmentResponse } from "@bw/shared/types/generated";

function attachmentContentUrl(attachmentId: string): string {
  return `/bff/api/community/message-attachments/${encodeURIComponent(attachmentId)}/content`;
}

function unavailableLabel(
  state: DirectMessageAttachmentResponse["state"],
): string {
  return state === "expired" ? "Attachment expired" : "Attachment unavailable";
}

/**
 * One privacy-safe renderer for private message descriptors. The only browser
 * URL is the authenticated BFF content route; S3 keys and public Media/CDN URLs
 * are intentionally not accepted as component props.
 */
export function MessageAttachments({
  attachments,
  compact = false,
}: {
  attachments: DirectMessageAttachmentResponse[] | undefined;
  compact?: boolean;
}) {
  if (!attachments?.length) return null;

  return (
    <Stack
      data-testid="message-attachments"
      direction={compact ? "column" : "row"}
      spacing={1}
      sx={{ mt: 0.5, maxWidth: compact ? "100%" : 680, flexWrap: "wrap" }}
    >
      {attachments.map((attachment) => {
        const readable = attachment.state === "attached";
        const filename = attachment.filename ?? "Attachment";
        if (!readable) {
          return (
            <Typography
              key={attachment.attachment_id}
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: "italic" }}
            >
              {unavailableLabel(attachment.state)}
            </Typography>
          );
        }
        if (attachment.kind === "image") {
          return (
            <Box
              key={attachment.attachment_id}
              component="a"
              href={attachmentContentUrl(attachment.attachment_id)}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "block",
                maxWidth: compact ? 240 : 360,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                overflow: "hidden",
                lineHeight: 0,
              }}
            >
              <Box
                component="img"
                src={attachmentContentUrl(attachment.attachment_id)}
                alt={filename}
                width={attachment.width ?? undefined}
                height={attachment.height ?? undefined}
                loading="lazy"
                sx={{
                  display: "block",
                  width: "100%",
                  maxHeight: compact ? 180 : 280,
                  objectFit: "contain",
                  bgcolor: "background.default",
                }}
              />
            </Box>
          );
        }
        return (
          <Link
            key={attachment.attachment_id}
            href={attachmentContentUrl(attachment.attachment_id)}
            underline="none"
            aria-label={`Download ${filename}`}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              maxWidth: "100%",
              px: 1,
              py: 0.75,
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              color: "text.primary",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <DescriptionOutlinedIcon fontSize="small" aria-hidden="true" />
            <Typography variant="body2" noWrap sx={{ maxWidth: 240 }}>
              {filename}
            </Typography>
            <DownloadRoundedIcon fontSize="small" aria-hidden="true" />
          </Link>
        );
      })}
    </Stack>
  );
}
